//----------------------------------------------------------------------------------------------------------------------
// Level Manager
//----------------------------------------------------------------------------------------------------------------------

import type { LoggerInterface } from '../interfaces/logger.ts';
import type {
    LevelConfig,
    LevelConstructor,
    LevelContext,
    PropertyHandler,
} from '../interfaces/level.ts';
import type { Disposable } from '../interfaces/lifecycle.ts';
import type {
    LevelCompletePayload,
    LevelErrorPayload,
    LevelProgressPayload,
} from '../events/payloads.ts';

import { Level } from '../classes/level.ts';
import { GameLevel } from '../classes/gameLevel.ts';
import { GameEventBus } from '../classes/eventBus.ts';
import type { GameEngine } from '../classes/gameEngine.ts';

// Utils
import { type LoggingUtility, SAGELogger } from '../utils/logger.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * Options for level transitions.
 */
export interface TransitionOptions
{
    /** If true, the old level is kept loaded (not disposed) after transitioning away */
    keepAlive ?: boolean;

    /** If true, the new level is loaded but not activated — currentLevel remains unchanged */
    preloadOnly ?: boolean;
}

//----------------------------------------------------------------------------------------------------------------------

/**
 * Manages game levels, acting as a factory for level instantiation.
 *
 * The LevelManager is responsible for:
 * - Registering level configurations and custom level classes
 * - Creating level instances with properly injected dependencies
 * - Managing the lifecycle of loaded levels (loading, activation, disposal)
 *
 * Levels are not instantiated directly. Instead, use the manager's factory methods:
 *
 * ```typescript
 * // Register a config
 * levelManager.registerLevelConfig({ name: 'Level1', scene: 'level1.glb' });
 *
 * // Register a custom level class
 * levelManager.registerLevelClass('custom', MyCustomLevel);
 *
 * // Load creates the instance with deps injected
 * await levelManager.loadLevel('Level1');
 * ```
 */
export class LevelManager implements Disposable
{
    private _eventBus : GameEventBus;
    private _gameEngine : GameEngine | null = null;
    private _log : LoggerInterface;
    private _logger ?: LoggingUtility;

    /** Map of registered level configurations by name */
    private _levelConfigs = new Map<string, LevelConfig>();

    /** Map of registered custom level classes by name */
    private _levelClasses = new Map<string, LevelConstructor>();

    /** Map of loaded level instances by name */
    private _loadedLevels = new Map<string, Level>();

    /** Map of registered property handlers for scene node metadata */
    private _propertyHandlers = new Map<string, PropertyHandler>();

    /** The currently active level */
    private _currentLevel : Level | null = null;

    /** Unsubscribe functions for event subscriptions */
    private _eventUnsubscribers : (() => void)[] = [];

    //------------------------------------------------------------------------------------------------------------------

    /**
     * Gets the currently active level
     */
    public get currentLevel() : Level | null
    {
        return this._currentLevel;
    }

    /**
     * Gets the registered property handlers
     */
    public get propertyHandlers() : Map<string, PropertyHandler>
    {
        return this._propertyHandlers;
    }

    //------------------------------------------------------------------------------------------------------------------

    constructor(
        eventBus : GameEventBus,
        logger ?: LoggingUtility
    )
    {
        this._eventBus = eventBus;
        this._logger = logger;
        this._log = logger?.getLogger('LevelManager') || new SAGELogger('LevelManager');

        // Subscribe to level events and store unsubscribe handles
        this._eventUnsubscribers.push(
            this._eventBus.subscribe('level:progress', (event) =>
            {
                this._handleProgress(event.payload);
            })
        );

        this._eventUnsubscribers.push(
            this._eventBus.subscribe('level:complete', (event) =>
            {
                this._handleComplete(event.payload);
            })
        );

        this._eventUnsubscribers.push(
            this._eventBus.subscribe('level:error', (event) =>
            {
                this._handleError(event.payload);
            })
        );

        this._log.info('LevelManager initialized');
    }

    //------------------------------------------------------------------------------------------------------------------
    // Internal API
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Late-bind the GameEngine after construction.
     * Called by createGameEngine() after the GameEngine instance is created.
     */
    $setGameEngine(gameEngine : GameEngine) : void
    {
        this._gameEngine = gameEngine;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Private Methods
    //------------------------------------------------------------------------------------------------------------------

    private _handleProgress(payload : LevelProgressPayload) : void
    {
        this._log.debug(`Level ${ payload.levelName } progress: ${ payload.progress }%`);
    }

    private _handleComplete(payload : LevelCompletePayload) : void
    {
        this._log.info(`Level ${ payload.levelName } loaded successfully`);
    }

    private _handleError(payload : LevelErrorPayload) : void
    {
        this._log.error(`Level ${ payload.levelName } error: ${ payload.message }`);
    }

    /**
     * Creates the LevelContext with all dependencies
     */
    private _createContext() : LevelContext
    {
        if(!this._gameEngine)
        {
            throw new Error('LevelManager: gameEngine not set. Call $setGameEngine() first.');
        }

        return {
            gameEngine: this._gameEngine,
            propertyHandlers: this._propertyHandlers,
            logger: this._logger,
        };
    }

    /**
     * Creates a Level instance from a config using the appropriate class
     */
    private _createLevelInstance(config : LevelConfig) : Level
    {
        const context = this._createContext();

        if(config.class)
        {
            // Look up the registered class
            const LevelClass = this._levelClasses.get(config.class);
            if(!LevelClass)
            {
                throw new Error(`Level class '${ config.class }' is not registered.`);
            }

            this._log.debug(`Creating level '${ config.name }' using class '${ config.class }'`);
            return new LevelClass(config, context) as Level;
        }

        // Use GameLevel as default
        this._log.debug(`Creating level '${ config.name }' using GameLevel`);
        return new GameLevel(config, context);
    }

    //------------------------------------------------------------------------------------------------------------------
    // Configuration Registration API
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Registers a level configuration.
     * The level will be instantiated when loadLevel() is called.
     *
     * @param config
     */
    public registerLevelConfig(config : LevelConfig) : void
    {
        if(this._levelConfigs.has(config.name))
        {
            this._log.warn(`Level config '${ config.name }' is already registered. Overwriting.`);
        }

        this._levelConfigs.set(config.name, config);
        this._log.info(`Registered level config: ${ config.name }`);
    }

    /**
     * Registers a custom Level class that can be referenced in configs.
     * When a config specifies `class: "my-level"`, this class will be instantiated.
     *
     * @param name - The key used in LevelConfig.class to reference this constructor
     * @param levelClass
     */
    public registerLevelClass(name : string, levelClass : LevelConstructor) : void
    {
        if(this._levelClasses.has(name))
        {
            this._log.warn(`Level class '${ name }' is already registered. Overwriting.`);
        }

        this._levelClasses.set(name, levelClass);
        this._log.info(`Registered level class: ${ name }`);
    }

    //------------------------------------------------------------------------------------------------------------------
    // Property Handler API
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Register a property handler for processing custom properties on scene nodes.
     * Handlers are called for each node that has the specified property in its metadata.
     *
     * @param property - The metadata property name to match (e.g., 'sound', 'collider')
     * @param handler
     */
    public registerPropertyHandler(property : string, handler : PropertyHandler) : void
    {
        if(this._propertyHandlers.has(property))
        {
            this._log.warn(`Property handler '${ property }' is already registered. Overwriting.`);
        }

        this._propertyHandlers.set(property, handler);
        this._log.debug(`Registered property handler: ${ property }`);
    }

    /**
     * Unregister a property handler.
     *
     * @param property
     */
    public unregisterPropertyHandler(property : string) : void
    {
        this._propertyHandlers.delete(property);
    }

    /**
     * Clear all registered property handlers.
     * Primarily useful for testing to ensure a clean state between tests.
     */
    public clearAllPropertyHandlers() : void
    {
        this._propertyHandlers.clear();
    }

    //------------------------------------------------------------------------------------------------------------------
    // Level Loading API
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Gets a loaded level by name.
     * Returns null if the level is not currently loaded.
     *
     * @param name
     */
    public getLevel(name : string) : Level | null
    {
        return this._loadedLevels.get(name) || null;
    }

    /**
     * Gets a registered level config by name.
     *
     * @param name
     */
    public getLevelConfig(name : string) : LevelConfig | null
    {
        return this._levelConfigs.get(name) || null;
    }

    /**
     * Loads a level by name. The level config must be registered first.
     *
     * @param name
     */
    public async loadLevel(name : string) : Promise<Level>;

    /**
     * Loads a level from a config. The config will be registered automatically.
     *
     * @param config
     */
    public async loadLevel(config : LevelConfig) : Promise<Level>;

    /**
     * Loads a level. If a name is passed, the config must be registered.
     * If a config is passed, it will be registered automatically.
     * Returns the existing instance if already loaded.
     *
     * @param configOrName
     */
    public async loadLevel(configOrName : string | LevelConfig) : Promise<Level>
    {
        // Resolve the config
        let config : LevelConfig;
        if(typeof configOrName === 'string')
        {
            const registered = this._levelConfigs.get(configOrName);
            if(!registered)
            {
                throw new Error(`Level config '${ configOrName }' is not registered.`);
            }
            config = registered;
        }
        else
        {
            config = configOrName;
            // Auto-register the config
            this.registerLevelConfig(config);
        }

        // Check if already loaded
        const existing = this._loadedLevels.get(config.name);
        if(existing)
        {
            this._log.warn(`Level '${ config.name }' is already loaded`);
            return existing;
        }

        // Create and load the level
        this._log.debug(`Loading level: ${ config.name }`);
        const level = this._createLevelInstance(config);
        await level.load();

        // Store the loaded level
        this._loadedLevels.set(config.name, level);

        return level;
    }

    /**
     * Activates a level by name, loading it if necessary.
     *
     * @param name
     */
    public async activateLevel(name : string) : Promise<Level>;

    /**
     * Activates a level from a config, loading it if necessary.
     *
     * @param config
     */
    public async activateLevel(config : LevelConfig) : Promise<Level>;

    /**
     * Activates a level, loading it if necessary.
     * Sets it as the currentLevel used by the render loop.
     *
     * @param configOrName
     */
    public async activateLevel(configOrName : string | LevelConfig) : Promise<Level>
    {
        const level = typeof configOrName === 'string'
            ? await this.loadLevel(configOrName)
            : await this.loadLevel(configOrName);
        this._currentLevel = level;
        this._log.info(`Activated level: ${ level.name }`);
        return level;
    }

    /**
     * Unloads a level and disposes its resources.
     * Clears currentLevel if this was the active level.
     *
     * @param name
     */
    public async unloadLevel(name : string) : Promise<void>
    {
        const level = this._loadedLevels.get(name);
        if(level)
        {
            // If this is the current level, clear it
            if(this._currentLevel === level)
            {
                this._currentLevel = null;
            }

            // Dispose the level
            await level.$dispose();

            // Remove from loaded levels
            this._loadedLevels.delete(name);

            this._log.info(`Unloaded level: ${ name }`);
        }
    }

    /**
     * Unloads the current level and disposes its resources
     */
    public async unloadCurrentLevel() : Promise<void>
    {
        if(this._currentLevel)
        {
            await this.unloadLevel(this._currentLevel.name);
        }
        else
        {
            this._log.warn('No current level to unload');
        }
    }

    //------------------------------------------------------------------------------------------------------------------
    // Transitions
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Orchestrates a level-to-level transition with lifecycle hooks and events.
     *
     * Flow: deactivate old -> load new -> dispose old (unless keepAlive) -> activate new.
     * If `preloadOnly` is true, the new level is loaded but not activated.
     *
     * @param levelName - The name of the level to transition to (must be registered)
     * @param options
     */
    public async transition(levelName : string, options ?: TransitionOptions) : Promise<void>
    {
        const oldLevel = this._currentLevel;

        try
        {
            // 1. Emit start
            this._eventBus.publish({
                type: 'level:transition:start',
                payload: {
                    from: oldLevel?.name,
                    to: levelName,
                },
            });

            // 2. Deactivate the old level
            if(oldLevel?.onDeactivate)
            {
                await oldLevel.onDeactivate();
            }

            // 3. Emit loading progress
            this._eventBus.publish({
                type: 'level:transition:progress',
                payload: {
                    stage: 'loading',
                    levelName,
                },
            });

            // 4. Load the new level
            const newLevel = await this.loadLevel(levelName);

            // 5. If preloadOnly, stop here
            if(options?.preloadOnly)
            {
                return;
            }

            // 6. Dispose old level unless keepAlive
            if(oldLevel && !options?.keepAlive)
            {
                await this.unloadLevel(oldLevel.name);
            }

            // 7. Set new level as current
            this._currentLevel = newLevel;

            // 8. Activate the new level
            if(newLevel.onActivate)
            {
                await newLevel.onActivate();
            }

            // 9. Emit complete
            this._eventBus.publish({
                type: 'level:transition:complete',
                payload: { levelName },
            });
        }
        catch (error)
        {
            // 10. Emit error event and re-throw
            this._eventBus.publish({
                type: 'level:transition:error',
                payload: {
                    from: oldLevel?.name,
                    to: levelName,
                    error,
                },
            });

            throw error;
        }
    }

    //------------------------------------------------------------------------------------------------------------------
    // Lifecycle
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Tears down the level manager and cleans up any resources
     */
    async $teardown() : Promise<void>
    {
        // Unsubscribe from all events
        for(const unsubscribe of this._eventUnsubscribers)
        {
            unsubscribe();
        }
        this._eventUnsubscribers = [];

        // Dispose of all loaded levels
        for(const level of this._loadedLevels.values())
        {
            // eslint-disable-next-line no-await-in-loop
            await level.$dispose();
        }

        this._loadedLevels.clear();
        this._levelConfigs.clear();
        this._currentLevel = null;
    }
}

//----------------------------------------------------------------------------------------------------------------------
